import {
  IdealBankElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Formik, Form } from 'formik';
import { useState } from 'react';
import { useTheme } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Label } from '@hivebrite/design-system/Label';

import {
  Text,
  Email,
} from 'hb-react/shared/components/formik/components/inputs';
import { STRIPE_PAYMENT_TYPES } from 'hb-react/shared/components/Payment/constants';
import { usePaymentContext } from 'hb-react/shared/components/Payment/context/PaymentContext';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

import './IdealBankSectionStyles.css';
import { Errors } from '../Errors';
import MandateText from '../MandateText';

const IDEAL_ELEMENT_OPTIONS = (theme) => ({
  style: {
    base: {
      padding: '10px 12px',
      color: theme.input.color,
      fontSize: '16px',
      '::placeholder': {
        color: theme.input.placeholder,
      },
    },
  },
});

const IdealPayment = () => {
  const {
    paymentAccountConfig,
    paymentEndpoint,
    paymentData,
    billingData,
    returnUrl,
    amountFormatted,
    isLoading,
    setLoading,
    hasRecurringItems,
  } = usePaymentContext();

  const [errors, setErrors] = useState(null);
  const stripe = useStripe();
  const elements = useElements();

  const theme = useTheme();

  const { type: gateway, merchant_display_name: merchantName } =
    paymentAccountConfig;

  const confirmPaymentIntent = async (
    clientSecret,
    accountholderName,
    accountholderEmail,
  ) => {
    const idealBank = elements.getElement(IdealBankElement);

    const billingDetails = {
      name: accountholderName,
    };

    if (hasRecurringItems) {
      billingDetails.email = accountholderEmail;
    }

    const { paymentIntent, error } = await stripe.confirmIdealPayment(
      clientSecret,
      {
        payment_method: {
          ideal: idealBank,
          billing_details: billingDetails,
        },
        return_url: returnUrl,
      },
    );

    if (error) {
      throw new Error(error);
    }

    return paymentIntent;
  };

  const setupIdealPayment = () =>
    axios.post(paymentEndpoint, {
      ...paymentData,
      billing: {
        payment_method: STRIPE_PAYMENT_TYPES.IDEAL,
        gateway,
      },
    });

  const handleSubmit = async ({ accountholderName, accountholderEmail }) => {
    setLoading(true);

    try {
      const { data } = await setupIdealPayment();

      const { client_secret: clientSecret, is_setup: isSetup } = data;
      const idealBank = elements.getElement(IdealBankElement);

      if (isSetup) {
        const result = await stripe.confirmIdealSetup(clientSecret, {
          payment_method: {
            ideal: idealBank,
            billing_details: {
              name: accountholderName,
              email: accountholderEmail,
            },
          },
          return_url: returnUrl,
        });

        if (result.error) {
          displayNotif(
            'error',
            s__(
              'StripeBancontact|An error occurred saving the payment details',
            ),
          );
          errorReporting.captureException(result.error);
        }
      } else {
        await confirmPaymentIntent(
          clientSecret,
          accountholderName,
          accountholderEmail,
        );
      }
    } catch (error) {
      if (error?.response?.data) {
        setErrors(error?.response?.data?.errors);
      }

      displayNotif(
        'error',
        s__('StripeIdeal|An error occurred processing the payment'),
      );
      errorReporting.captureException(error);
    }

    setLoading(false);
  };

  const initialValues = {
    accountholderName: billingData?.name || '',
    accountholderEmail: billingData?.email || '',
  };

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validateOnMount
      validate={({ ideal }) => {
        if (!ideal) {
          return { ideal: 'Required' };
        }
      }}
    >
      {({ isValid, setFieldValue }) => (
        <Form>
          {errors && <Errors errors={errors} />}
          <Box flexDirection="column">
            <Box data-testid="stripe-ideal-wrapper" mb={4} display="block">
              <Label htmlFor="ideal-bank-element" required>
                {s__('StripeIdeal|Bank')}
              </Label>
              <IdealBankElement
                id="ideal-bank-element"
                options={IDEAL_ELEMENT_OPTIONS(theme)}
                onChange={({ complete }) => setFieldValue(`ideal`, complete)}
              />
            </Box>
            <Box flexDirection="column" mb={2}>
              <Text
                name="accountholderName"
                label={s__('StripeIdeal|Accountholder Name')}
                validate={{
                  required: true,
                }}
              />
              {hasRecurringItems && (
                <>
                  <Email
                    name="accountholderEmail"
                    label={s__('StripeIdeal|Accountholder Email')}
                    validate={{
                      required: true,
                    }}
                  />
                  <MandateText mb={6} merchantDisplayName={merchantName} />
                </>
              )}

              <Box
                justifyContent="space-between"
                alignContent="center"
                width="100%"
                mt={3}
              >
                <Box ml="auto" pl={3}>
                  <Button
                    dataTestId="stripe-sepa-debit-form-submit"
                    type="submit"
                    isLoading={isLoading}
                    isDisabled={!isValid}
                  >
                    {__('Pay %{amountFormatted}').i__({
                      amountFormatted,
                    })}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default IdealPayment;
